import { defineStore } from 'pinia'
import { ref } from 'vue'

import { creativeAiApi } from '../api/CreativeAiApi'
import type {
  BenchmarkCreativesQuery,
  BenchmarkCreativesResult,
  BenchmarkCreativesScope,
} from '../models/benchmarkCreatives.types'
import { decodeBenchmarkCreativesResponse } from '../service/benchmarkCreatives'

export interface BenchmarkCreativesLoadInput {
  scope: BenchmarkCreativesScope
  query: BenchmarkCreativesQuery
}

/**
 * Legacy parity (`fetchBenchmarkCounts`'s `unique` map): the dedup key is `featureKey__featureValue`
 * only — deliberately not scoped by platform/objective/campaignType/metric, matching old exactly.
 * Two rows recommending the same switch under different segment filters share one cached count.
 */
function cacheKey(query: BenchmarkCreativesQuery): string {
  return `${query.analysisId ?? 'all'}__${query.featureKey}__${query.featureValue}`
}

/**
 * A keyed cache, not a single-slot request owner (unlike `whatsWorkingStore`/`combinationsStore`):
 * Opportunities Analysis needs one independent lookup per row's own benchmark suggestion, all
 * fetched in parallel and never superseding each other.
 */
export const useBenchmarkCreativesStore = defineStore('creativeAiBenchmarkCreatives', () => {
  const results = ref<Record<string, BenchmarkCreativesResult>>({})
  const pending = new Map<string, Promise<void>>()

  async function ensure(input: BenchmarkCreativesLoadInput): Promise<void> {
    const key = cacheKey(input.query)
    if (key in results.value) return
    const existing = pending.get(key)
    if (existing) {
      await existing
      return
    }
    const request = (async () => {
      try {
        const raw = await creativeAiApi.getBenchmarkCreatives(input.scope, {
          type: input.query.kind,
          feature_key: input.query.featureKey,
          feature_value: input.query.featureValue,
          ...(input.query.platform ? { platform: input.query.platform } : {}),
          ...(input.query.objective ? { objective: input.query.objective } : {}),
          ...(input.query.campaignType ? { campaign_type: input.query.campaignType } : {}),
          ...(input.query.metric ? { metric: input.query.metric } : {}),
          ...(input.query.analysisId !== undefined ? { analysis_id: input.query.analysisId } : {}),
        })
        results.value = { ...results.value, [key]: decodeBenchmarkCreativesResponse(raw) }
      } catch {
        results.value = { ...results.value, [key]: { count: 0, creativeIds: [] } }
      } finally {
        pending.delete(key)
      }
    })()
    pending.set(key, request)
    await request
  }

  function get(
    analysisId: number | undefined,
    featureKey: string,
    featureValue: string,
  ): BenchmarkCreativesResult | null {
    return results.value[`${analysisId ?? 'all'}__${featureKey}__${featureValue}`] ?? null
  }

  function reset(): void {
    results.value = {}
    pending.clear()
  }

  return { results, ensure, get, reset }
})
